import { navigateTo } from '@app/core/routing';
import { useStation } from '@app/core/stations/hooks/useStation';
import { webApi } from '@mi-front/api-clients';
import { formatDate, formatDuration, rangeDuration } from '@mi-front/core';
import { Box, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import {
  DownloadMediaNotificationCompletedView,
  DownloadMediaNotificationErrorView,
  DownloadMediaNotificationLoadingView,
  DownloadMediaNotificationPendingView,
} from './NotificationViews';

export interface DownloadMediaJobNotificationProps {
  jobId: string;
  /** Station id; used with useStation to build the static notification title. */
  stationId: string;
  /** Range start (ISO); used for title "{station} - {HH:mm} ({duration})". */
  from: string;
  /** Range end (ISO); used for duration in title. */
  to: string;
  /** Called when the toast should be dismissed (e.g. after user clicks Download). */
  onClose?: () => void;
}

/**
 * Toast content that polls a download job, shows progress, and offers a download button when completed.
 * Rendered via react-toastify (same as UI notify). Call onClose to dismiss the toast.
 */
export function DownloadMediaJobNotification({
  jobId,
  stationId,
  from,
  to,
  onClose,
}: DownloadMediaJobNotificationProps) {
  const [downloading, setDownloading] = useState(false);
  const station = useStation(stationId);
  const title = useMemo(() => {
    const hhmm = formatDate(from, 'HH:mm');
    const durationStr = formatDuration(rangeDuration(from, to));
    return station?.name ? `${station.name} ${hhmm} (${durationStr})` : `${hhmm} (${durationStr})`;
  }, [from, to, station?.name]);

  const {
    data: job,
    isLoading,
    isError,
  } = webApi.useGetMediaExportJobsById(jobId, {
    query: {
      refetchInterval: (query) => {
        const d = query.state.data;
        if (!d) {
          return 2000;
        }

        if (d.isFinished) {
          return false;
        }

        return 2000;
      },
    },
  });

  const handleDownload = useCallback(() => {
    setDownloading(true);
    webApi
      .getMediaExportJobsDownloadUrl(jobId)
      .then(({ url }) => {
        onClose?.();
        navigateTo(url);
      })
      .catch(() => {
        setDownloading(false);
      });
  }, [jobId, onClose]);

  const content =
    isLoading && !job ? (
      <DownloadMediaNotificationLoadingView />
    ) : job?.isPending ? (
      <DownloadMediaNotificationPendingView job={job} />
    ) : isError || job?.status !== 'COMPLETED' ? (
      <DownloadMediaNotificationErrorView />
    ) : (
      <DownloadMediaNotificationCompletedView
        onDownload={handleDownload}
        downloading={downloading}
        fileSizeBytes={job.result?.fileSize}
      />
    );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography variant="caption" mb={0.5}>
        {title}
      </Typography>
      {content}
    </Box>
  );
}
